import path from 'path';
import querystring from 'querystring';

import Logger from '@cvent/nucleus-logging';
const LOG = new Logger('event-guestside-site/src/main');

import loadCookieConsent from './appInitialization/loadCookieConsent';
import history from './myHistory';
import { createRouteHandlers } from './appInitialization/routeHandlers';
import { loadCustomFonts } from 'nucleus-widgets/redux/modules/customFonts';
import { setCurrencyCode, setLocale } from 'nucleus-guestside-site/src/redux/modules/text';
import { withRouteOnHandler } from './appInitialization/routeHandlerComponent';
import { createApp } from './App';
import { loadLandingPageContent, loadLanguageManagementSettings } from './redux/actions';
import { loadEventTimezoneIntoStore } from './redux/timezones';

import React from 'react';
import { render } from 'react-dom';

import { Provider } from 'react-redux';
import Title from './appInitialization/Title';
import {
  Route,
  Redirect,
  Switch,
  Router
} from 'react-router-dom';
import DialogContainer from 'nucleus-guestside-site/src/containers/DialogContainer';

// Get the event widget factory.
import WidgetFactory from './widgetFactory';
import LoadingProgressIndicatorWrapper from './appInitialization/LoadingProgressIndicatorWrapper';

import { initializeFetchWithSessionTimeout } from './dialogs/SessionTimedOutDialog';
import configureStore from './redux/configureStore';
import TravelCartClient from './clients/TravelCartClient';
import EventEmailClient from './clients/EventEmailClient';
import PasskeyClient from './clients/PasskeyClient';
import FlexFileClient from './clients/FlexFileClient';
import DataTagsResolutionClient from './clients/DataTagsResolutionClient';
import EventGuestClient from './clients/EventGuestClient';
import CreditCardClient from './clients/CreditCardClient';
import EventPersonaClient from './clients/EventPersonaClient';
import locales from './appInitialization/locales';
import { loadIntlWithLocale } from 'nucleus-text/intlLoaders/index';
import getMatchingLocale from 'nucleus-text/util/getMatchingLocale';
import { createLoadAccountSnapshotAction, createLoadEventSnapshotAction } from './redux/actions';
import { loadTravelSnapshotDataIntoStore } from 'event-widgets/redux/modules/eventTravel';
import { loginRegistrant } from './redux/registrantLogin/actions';
import { setCurrencies } from './redux/currencies';
import { loadCapacityCounts } from 'event-widgets/redux/modules/capacity';
import { setAirports } from './redux/airports';

// testSettings allows settings to be changed in services by passing in query params.
// For example, we can set the dequeueTimeout by passing a parameter.
function getTestSettings(queryParams) {
  return {
    registrationCheckoutTimeout: queryParams.registrationCheckoutTimeout
  };
}

function getUserType(eventContext) {
  if (eventContext.isPlanner) {
    return 'planner';
  } else if (eventContext.isPreview) {
    return 'preview';
  }
  return 'standard';
}

// Register Translations
async function registerTranslations(textResolverFn, cultureCode) {
  const translationsPromise = new Promise(getMatchingLocale(cultureCode, locales));
  await new Promise(resolve => loadIntlWithLocale(cultureCode, resolve));
  textResolverFn.registerTranslations(await translationsPromise, 'default', cultureCode);
}

async function startAppRender(appSettings, preloadedData) {
  render(
    <LoadingProgressIndicatorWrapper />,
    document.getElementById('react-mount')
  );

  const viewRoot = window && window.location && window.location.pathname
    && ('/' + window.location.pathname.split('/')[1]) || appSettings.viewRoot;
  const queryParams = querystring.parse(window.location.search.slice(1));
  const testSettings = appSettings.eventContext.allowDevMode ? getTestSettings(queryParams) : {};
  const eventId = appSettings.eventContext.eventId;
  const plannerRegViewRoot = appSettings.viewRoot;
  const normalRegViewRoot = path.join(viewRoot, eventId);
  const rootPath = appSettings.eventContext.isPlanner ? plannerRegViewRoot : normalRegViewRoot;
  const baseUrl = `${appSettings.serviceBaseUrl}/v1/`;
  const calendarPath = `${baseUrl}event/ical/${eventId}?environment=${appSettings.environment}`;
  const analyticsUrl = appSettings.analyticsUrl;
  const analyticsUrlPath = appSettings.analyticsUrlPath;
  let eventLaunchWizardSettings = appSettings.eventLaunchWizardSettings;
  const httpReferrer = document.referrer;

  if (!eventLaunchWizardSettings) {
    eventLaunchWizardSettings = {};
  } else {
    eventLaunchWizardSettings = JSON.parse(eventLaunchWizardSettings);
  }
  const { isMerchantCPS = false, showBankAccount = false, merchantAccountId = null,
    openLaunchWizard = false, remittanceType = '' } = eventLaunchWizardSettings;

  const userType = getUserType(appSettings.eventContext);

  const store = configureStore({
    account: { name: appSettings.accountName },
    accessToken: appSettings.accessToken,
    environment: appSettings.environment,
    testSettings,
    clients: {
      eventEmailClient: new EventEmailClient(baseUrl, eventId, appSettings.environment),
      lookupClient: preloadedData.clients.lookupClient,
      capacityClient: preloadedData.clients.capacityClient,
      regCartClient: preloadedData.clients.regCartClient,
      eventSnapshotClient: preloadedData.clients.eventSnapshotClient,
      eventGuestClient: new EventGuestClient(baseUrl, eventId, appSettings.environment),
      travelCartClient: new TravelCartClient(baseUrl, eventId, appSettings.environment, userType),
      eventPersonaClient: new EventPersonaClient(baseUrl, eventId, appSettings.environment, userType),
      flexFileClient: new FlexFileClient(baseUrl, eventId, appSettings.environment),
      DataTagsResolutionClient: new DataTagsResolutionClient(baseUrl, eventId, appSettings.environment,
        appSettings.eventContext.isPreview),
      creditCardClient: new CreditCardClient(baseUrl, eventId, appSettings.environment, userType),
      passkeyClient: new PasskeyClient(baseUrl, eventId, appSettings.environment)
    },
    googleMap: {
      apiKey: appSettings.googleApiKey
    },
    pathInfo: {
      rootPath,
      eventId: appSettings.eventContext.eventId,
      pageId: '',
      navigationDialogConfig: { isOpen: false },
      calendarPath
    },
    userSession: {
      eventId: appSettings.eventContext.eventId,
      inviteeId: appSettings.eventContext.inviteeId,
      contactId: appSettings.eventContext.contactId,
      isPreview: appSettings.eventContext.isPreview,
      isPlanner: appSettings.eventContext.isPlanner,
      isTestMode: appSettings.eventContext.isTestMode,
      defaultRegTypeId: appSettings.eventContext.registrationTypeId,
      defaultRegPathId: appSettings.eventContext.registrationPathId,
      defaultRegPackId: appSettings.eventContext.registrationPackId,
      regCartId: appSettings.eventContext.regCartId,
      referenceId: appSettings.eventContext.referenceId,
      confirmationNumber: appSettings.eventContext.confirmationNumber,
      showEventBuildWizardBanner: appSettings.eventContext.showEventBuildWizardBanner || false,
      freeTrialPurchaseCta: appSettings.eventContext.freeTrialPurchaseCta || '',
      eventBuildWizardExitUrl: queryParams.eventBuildWizardExit || '',
      licenseTypeId: appSettings.eventContext.licenseTypeId || 1,
      isFreeTrial: appSettings.eventContext.isFreeTrial || false,
      isPlannerRegMod: queryParams.plannerRegMod === 'true',
      httpReferrer: httpReferrer || ''
    },
    plannerRegSettings: appSettings.plannerRegSettings,
    eventLaunchWizardSettings: {
      isMerchantCPS,
      showBankAccount,
      merchantAccountId,
      openLaunchWizard,
      remittanceType
    },
    eventSnapshotVersion: appSettings.eventSnapshotVersion,
    widgetFactory: new WidgetFactory()
  }, {
    baseUrl: analyticsUrl,
    endpoint: analyticsUrlPath
  });
  initializeFetchWithSessionTimeout(store);

  store.dispatch(createLoadAccountSnapshotAction(preloadedData.assets.accountSnapshot));
  store.dispatch(createLoadEventSnapshotAction(preloadedData.assets.eventSnapshot, store.getState().account));
  store.dispatch(setLocale(appSettings.cultureCode));

  if (preloadedData.assets.loginInfo) {
    await store.dispatch(loginRegistrant(preloadedData.assets.loginInfo));
  }
  // else user is logged in whenever session is alive, or should manually login through AlreadyRegisteredDialog

  const { text, event: { eventCurrencySnapshot: { isoAlphabeticCode } },
    account: { customFonts = [] } } = store.getState();
  await registerTranslations(text.resolver, appSettings.cultureCode);

  store.dispatch(loadTravelSnapshotDataIntoStore(preloadedData.assets.travelSnapshot, appSettings.isTravelEnabled));
  store.dispatch(loadEventTimezoneIntoStore(appSettings.eventTimezoneId, preloadedData.assets.eventTimezone.timeZones,
    preloadedData.assets.eventTimezone.translations));
  store.dispatch(setCurrencies(preloadedData.assets.currencies));
  store.dispatch(loadCapacityCounts(preloadedData.assets.capacity));
  store.dispatch(setAirports(preloadedData.assets.airports));

  const activeCustomFonts = customFonts.filter(font => font.isActive);
  if (activeCustomFonts.length > 0) {
    store.dispatch(loadCustomFonts(activeCustomFonts));
  }
  store.dispatch(setCurrencyCode(isoAlphabeticCode));
  store.dispatch(loadLanguageManagementSettings());

  await store.dispatch(loadLandingPageContent(appSettings.eventContext.landingPageVariety));

  const App = createApp(appSettings.assetRoot || '/');
  const routeHandlers = createRouteHandlers(store);

  let fixtureRoute = null;
  if (process.env.NODE_ENV !== 'production') {
    fixtureRoute = <Redirect path={`${rootPath}/`} to={`/${appSettings.eventContext.eventId}`} component={App}/>;
  }
  render(
    <Provider store={store}>
      <div>
        <Title />
        <Router history={history}>
          <Switch>
            <Route
              exact
              path={`${rootPath}/register`}
              component={withRouteOnHandler(routeHandlers.startRegistration, routeHandlers.pageLeave)(App)}
            />
            <Route
              exact
              path={`${rootPath}/modifyRegistration`}
              component={withRouteOnHandler(routeHandlers.modifyRegistration, routeHandlers.pageLeave)(App)}
            />
            <Route
              exact
              path={`${rootPath}/cancelRegistration`}
              component={withRouteOnHandler(routeHandlers.cancelRegistration, routeHandlers.pageLeave)(App)}
            />
            <Route
              exact
              path={`${rootPath}/decline`}
              component={withRouteOnHandler(routeHandlers.declineRegistration, routeHandlers.pageLeave)(App)}
            />
            <Route
              exact
              path={`${rootPath}/opt-out`}
              component={withRouteOnHandler(routeHandlers.optOut, routeHandlers.pageLeave)(App)}
            />
            <Route
              exact
              path={`${rootPath}/unsubscribe`}
              component={withRouteOnHandler(routeHandlers.unsubscribe, routeHandlers.pageLeave)(App)}
            />
            <Route
              exact
              path={`${rootPath}/waitlist`}
              component={withRouteOnHandler(routeHandlers.waitlist, routeHandlers.pageLeave)(App)}
            />
            <Route
              exact
              path={`${rootPath}/confirmationpage`}
              component={withRouteOnHandler(routeHandlers.confirmationPage, routeHandlers.pageLeave)(App)}
            />
            <Route
              exact
              path={`${rootPath}/:pageId`}
              component={withRouteOnHandler(routeHandlers.page, routeHandlers.pageLeave)(App)}
            />
            <Route
              exact
              path={`${rootPath}/`}
              component={withRouteOnHandler(routeHandlers.index, routeHandlers.pageLeave)(App)}
            />
            {fixtureRoute}
          </Switch>
        </Router>
        <DialogContainer
          spinnerMessage="EventGuestSide_SharedLoadingProgressWidget_SpinnerLoadingText__resx"
          message="EventGuestSide_SharedLoadingProgressWidget_MessageText__resx"
        />
      </div>
    </Provider>, document.getElementById('react-mount'));

  loadCookieConsent(store.getState());

  // remove devtools if browser plugin https://github.com/zalmoxisus/redux-devtools-extension present.
  if (process.env.NODE_ENV !== 'production') {
    if (window.devToolsExtension) {
      LOG.info('using browser extension redux dev tools instead of normal redux dev tools');
    } else {
      const target = document.getElementById('react-mount');
      const devTarget = document.createElement('div');
      const DevTools = require('nucleus-guestside-site/src/redux/DevTools').default;
      const devToolsElement = (
        <Provider store={store}>
          <DevTools />
        </Provider>
      );
      if (target && target.parentNode) {
        target.parentNode.insertBefore(devTarget, target.nextSibling);
      } else {
        LOG.error('failed to insert devtools into DOM');
      }
      render(devToolsElement, devTarget);
    }
  }
}

export default startAppRender;
